 
import React, { useEffect, useState } from "react";
import api from "../../api/api";

const DeliveryPendingView = ({ setView, context }) => {
  const [orders, setOrders] = useState([]); // Holds the fetched orders
  const [vendorInfo, setVendorInfo] = useState(null); // Holds Table1 vendor info
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [remark, setRemark] = useState(""); // Remark input

  // Get POID and UserName from localStorage
  const Poid = localStorage.getItem("poidContent");
  const Usrname = localStorage.getItem("username");

  useEffect(() => {
    if (!Poid || !Usrname) {
      setError("Missing PO ID or User information.");
      setLoading(false);
      return;
    }

    const fetchOrderItems = async () => {
      try {
        setLoading(true);

        const response = await api.get("GetPoOrderItemdeliverylistAPI", {
          Poid,
          User: Usrname,
        });
        const items = response?.Table || [];
        const vendor = response?.Table1?.[0] || null;

        if (items.length === 0) {
          setError("No items found for this order.");
        } else {
          setOrders(
            items.map((item) => ({
              slno: item.Slno,
              item: item.Partno,
              OrdQty: item.Qty,
            }))
          );
          setError(null);
        }

        if (vendor) {
          setVendorInfo(vendor);
        }
      } catch (err) {
        setError("Failed to load order items.");
      } finally {
        setLoading(false);
      }
    };

    const fetchDeliveryStatus = async () => {
      try {
        const resp = await api.EditOrderDeliveryStatus2(Poid);
        const deliveryNote = resp?.Table?.[0]?.DeliveryNote ?? "";
        setRemark(deliveryNote.trim());
      } catch (err) {
        console.error("Error fetching delivery status:", err);
      }
    };

    fetchOrderItems();
    fetchDeliveryStatus();
  }, [Poid, Usrname]);

  const handlePackedClick = async () => {
    if (!Poid || !Usrname) {
      alert("Missing required data for packing status update.");
      return;
    }

    try {
      const response = await api.UpdateOrderDeliveryPackStatus(Poid, 1, Usrname);
      if (response?.Table?.[0]?.Status === "success") {
        if (response?.Table1?.[0]?.Status === "Packed") {
          alert("Packing status updated successfully!");
        } else {
          alert('Failed to update packing status. Not set to "Packed".');
        }
      } else {
        alert("Failed to update packing status.");
      }
    } catch (error) {
      alert("Failed to update packing status.");
    }
  };

  const handleDeliveryClick = async () => {
    if (!Poid || !Usrname) {
      alert("Missing required data for delivery status update.");
      return;
    }

    if (!remark.trim()) {
      alert("Please enter a remark before marking as Delivered.");
      return;
    }

    try {
      const response = await api.UpdateOrderDeliveryStatus2API(
        Poid,
        remark,
        Usrname
      );

      if (response?.Table?.[0]?.Status === "success") {
        alert("Order marked as Delivered successfully!");
        setView("DeliveryList");
      } else {
        alert("Failed to update delivery status.");
      }
    } catch (error) {
      console.error("Delivery update error:", error);
      alert("Failed to update delivery status.");
    }
  };

  const handleCancelClick = () => {
    alert(`Order cancelled with remark: ${remark}`);
    setView("DeliveryList");
  };

  const handleSendWhatsApp = async () => {
    if (!remark.trim()) {
      alert("Remark is empty. Please enter something before sending.");
      return;
    }

    try {
      const response = await api.get("GetPoOrderItemdeliverylistAPI", {
        Poid,
        User: Usrname,
      });

      const phoneNumber = response?.Table1?.[0]?.VCode;

      if (!phoneNumber) {
        alert("Phone number not found in API response.");
        return;
      }

      const message = `Order Remark: ${remark}`;
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
        message
      )}`;
      console.log("whatsappUrl", whatsappUrl);
      window.open(whatsappUrl, "_blank");
    } catch (error) {
      console.error("Error fetching phone number:", error);
      alert("Failed to send WhatsApp message. Please try again.");
    }
  };

  return (
    <div className="max-w-6xl p-8 mx-auto bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border border-gray-200">
      <h2 className="mb-6 text-2xl font-bold text-gray-800 flex items-center gap-2">
         Pending Orders
      </h2>

      {vendorInfo && (
        <div className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200">
          <p className="text-lg font-semibold text-blue-800">
            {vendorInfo.Customerdetails}
          </p>
        </div>
      )}

      {loading && <p className="text-gray-500">Loading order items...</p>}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Orders Table */}
          <div className="overflow-hidden border rounded-lg shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-700 text-left">
                  <th className="px-4 py-2 border">Slno</th>
                  <th className="px-4 py-2 border">Item</th>
                  <th className="px-4 py-2 border">Ord. Qty</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="p-4 text-center text-gray-500 italic"
                    >
                      No order items found.
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr
                      key={order.slno}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-2 border">{order.slno}</td>
                      <td className="px-4 py-2 border">{order.item}</td>
                      <td className="px-4 py-2 border">{order.OrdQty}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Remark Box */}
          <div className="flex flex-col">
            <label className="mb-2 font-medium text-gray-700">Remark</label>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={6}
              className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Enter remark here..."
            />
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap justify-between gap-4 mt-8">
  <div className="flex gap-2">
    <button
      onClick={handlePackedClick}
      className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition"
    >
      ✅ Packed
    </button>
    <button
      onClick={() => setView("DeliveryList")}
      className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 shadow-sm transition"
    >
      ❌ Close
    </button>
  </div>

  <div className="flex gap-2">
    <button
      onClick={handleDeliveryClick}
      className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 shadow-sm transition"
    >
      🚚 Delivered
    </button>
    {/* <button
      onClick={handleCancelClick}
      className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow-sm transition"
    >
      ❌ Cancel
    </button> */}
    <button
      onClick={handleSendWhatsApp}
      className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-green-500 hover:bg-green-600 shadow-sm transition"
    >
      WhatsApp
    </button>
  </div>
</div>

    </div>
  );
};

export default DeliveryPendingView;




// import React, { useEffect, useState } from 'react';
// import api from '../../api/api';

// const DeliveryPendingView = ({ setView, context }) => {
//   const [orders, setOrders] = useState([]); // Holds the fetched orders
//   const [vendorInfo, setVendorInfo] = useState(null); // Holds Table1 vendor info
//   const [loading, setLoading] = useState(true); // Loading state
//   const [error, setError] = useState(null); // Error state
//   const [remark, setRemark] = useState(''); // Remark input

//   // Get POID and UserName from localStorage
//   const Poid = localStorage.getItem('poidContent');
//   const Usrname = localStorage.getItem('username');

//   useEffect(() => {
//     if (!Poid || !Usrname) {
//       setError('Missing PO ID or User information.');
//       setLoading(false);
//       return;
//     }

//     const fetchOrderItems = async () => {
//       try {
//         setLoading(true);

//         // ✅ Updated API call
//         const response = await api.get("GetPoOrderItemdeliverylistAPI", { Poid, User: Usrname });
//         const items = response?.Table || [];
//         const vendor = response?.Table1?.[0] || null;

//         if (items.length === 0) {
//           setError('No items found for this order.');
//         } else {
//           setOrders(items.map(item => ({
//             slno: item.Slno,
//             item: item.Partno,
//             OrdQty: item.Qty,
//           })));
//           setError(null);
//         }

//         if (vendor) {
//           setVendorInfo(vendor);
//         }
//       } catch (err) {
//         setError('Failed to load order items.');
//       } finally {
//         setLoading(false);
//       }
//     };

//     const fetchDeliveryStatus = async () => {
//       try {
//         const resp = await api.EditOrderDeliveryStatus2(Poid);
//         const deliveryNote = resp?.Table?.[0]?.DeliveryNote ?? '';
//         setRemark(deliveryNote.trim());
//       } catch (err) {
//         console.error("Error fetching delivery status:", err);
//       }
//     };

//     fetchOrderItems();
//     fetchDeliveryStatus();
//   }, [Poid, Usrname]);

//   const handlePackedClick = async () => {
//     if (!Poid || !Usrname) {
//       alert('Missing required data for packing status update.');
//       return;
//     }

//     try {
//       const response = await api.UpdateOrderDeliveryPackStatus(Poid, 1, Usrname);
//       if (response?.Table?.[0]?.Status === 'success') {
//         if (response?.Table1?.[0]?.Status === 'Packed') {
//           alert('Packing status updated successfully!');
//         } else {
//           alert('Failed to update packing status. Not set to "Packed".');
//         }
//       } else {
//         alert('Failed to update packing status.');
//       }
//     } catch (error) {
//       alert('Failed to update packing status.');
//     }
//   };

//     const handleDeliveryClick = async () => {
//     if (!Poid || !Usrname) {
//       alert("Missing required data for delivery status update.");
//       return;
//     }

//     // ✅ New validation: stop if remark is empty
//     if (!remark.trim()) {
//       alert("Please enter a remark before marking as Delivered.");
//       return;
//     }

//     try {
//       // Ensure you send PoId (capital I)
//       const response = await api.UpdateOrderDeliveryStatus2API(Poid, remark, Usrname);

//       if (response?.Table?.[0]?.Status === "success") {
//         alert("Order marked as Delivered successfully!");
//         setView("DeliveryList");
//       } else {
//         alert("Failed to update delivery status.");
//       }
//     } catch (error) {
//       console.error("Delivery update error:", error);
//       alert("Failed to update delivery status.");
//     }
//   };


//   const handleCancelClick = () => {

//   // localStorage.removeItem('poidContent');
//   // localStorage.removeItem('username');
//   // alert("LocalStorage cleared!");
  
//     alert(`Order cancelled with remark: ${remark}`);
//     setView('DeliveryList');
//   };

//   // ✅ Fixed WhatsApp function
//   const handleSendWhatsApp = async () => {
//     if (!remark.trim()) {
//       alert("Remark is empty. Please enter something before sending.");
//       return;
//     }

//     try {
//       // Call your API with proper params
//       const response = await api.get("GetPoOrderItemdeliverylistAPI", { Poid, User: Usrname });

//       // Extract phone number from Table1 -> Vcode
//       const phoneNumber = response?.Table1?.[0]?.VCode;

//       if (!phoneNumber) {
//         alert("Phone number not found in API response.");
//         return;
//       }

//       // Format the message
//       const message = `Order Remark: ${remark}`;

//       // WhatsApp link with phone number and message
//       const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
//       console.log("whatsappUrl", whatsappUrl);

//       // Open in new tab
//       window.open(whatsappUrl, "_blank");
//     } catch (error) {
//       console.error("Error fetching phone number:", error);
//       alert("Failed to send WhatsApp message. Please try again.");
//     }
//   };

//   return (
//     <div className="max-w-5xl p-6 mx-auto bg-white rounded shadow-md">
//       <h2 className="mb-4 text-xl font-semibold text-gray-700">Pending Orders</h2>

//       {/* ✅ Added vendor info from Table1 */}
//       {vendorInfo && (
//         <div className="p-3 mb-4 bg-gray-100 rounded">
//           <p> <strong>{vendorInfo.Customerdetails}</strong> </p>
//         </div>
//       )}

//       {loading && <p>Loading order items...</p>}
//       {error && <p className="text-red-600">{error}</p>}

//       {!loading && !error && (
//         <div className="grid grid-cols-2 gap-4">
//           {/* Orders Table */}
//           <table className="w-full border border-collapse border-gray-300 table-auto">
//             <thead>
//               <tr className="bg-gray-100">
//                 <th className="px-3 py-1 text-left border border-gray-300">Slno</th>
//                 <th className="px-3 py-1 text-left border border-gray-300">Item</th>
//                 <th className="px-3 py-1 text-left border border-gray-300">Ord. Qty</th>
//               </tr>
//             </thead>
//             <tbody>
//               {orders.length === 0 ? (
//                 <tr>
//                   <td colSpan={3} className="p-4 text-center">No order items found.</td>
//                 </tr>
//               ) : (
//                 orders.map((order) => (
//                   <tr key={order.slno}>
//                     <td className="px-3 py-1 border border-gray-300">{order.slno}</td>
//                     <td className="px-3 py-1 border border-gray-300">{order.item}</td>
//                     <td className="px-3 py-1 border border-gray-300">{order.OrdQty}</td>
//                   </tr>
//                 ))
//               )}
//             </tbody>
//           </table>

//           {/* Remark Box */}
//           <div>
//             <label className="block mb-2 font-medium text-gray-700">Remark</label>
//             <textarea
//               value={remark}
//               onChange={(e) => setRemark(e.target.value)}
//               rows={6}
//               className="w-full p-2 border border-gray-300 rounded"
//               placeholder="Enter remark here..."
//             />
//           </div>
//         </div>
//       )}

//       {/* Action Buttons */}
//       <div className="flex justify-between gap-3 mt-6">
//         <div className="flex gap-3">
//           <button
//             onClick={handlePackedClick}
//             className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700"
//           >
//             Packed
//           </button>
//           <button
//             onClick={() => setView('DeliveryList')}
//             className="px-4 py-2 text-white bg-gray-600 rounded hover:bg-gray-700"
//           >
//             Close
//           </button>
//         </div>

//         <div className="flex gap-3">
//           <button
//             onClick={handleDeliveryClick}
//             className="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700"
//           >
//             Delivered
//           </button>
//           <button
//             onClick={handleCancelClick}
//             className="px-4 py-2 text-white bg-red-600 rounded hover:bg-red-700"
//           >
//             Cancel
//           </button>

//           {/* ✅ WhatsApp button sends remark */}
//           <button
//             onClick={handleSendWhatsApp}
//             className="px-4 py-2 text-white bg-green-500 rounded hover:bg-green-600"
//           >
//             WhatsApp 🟢
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default DeliveryPendingView;